import React, { createContext, useContext, useEffect, useState } from "react";
import { useFirestore, useFunctions, useStorage } from "reactfire";
import random from "randomstring";
import { Country } from "../../shared/constants/Constant";
import { SubsectionStatus, useAppUser } from "../user-context/UserContext";
import { MediaFileContent } from "../../screens/dashboard/containers/update-workshops/components/media-card/MediaCard";
import firebase from "firebase/app";

export enum WorkshopSectionContentType {
  TEXT = "Text",
  FILE_URL = "FileUrl",
  VIDEO_URL = "VideoUrl",
  IMAGE_URL = "ImageUrl",
  URL = "Url",
}

export interface WorkshopSectionContent {
  type: WorkshopSectionContentType;
  content: string;
  fileName?: string;
  id?: string;
  wached?: boolean;
  orderNo: number;
  mode: "EDIT" | "PREVIEW" | "NEW";
}

export interface WorkshopSection {
  id?: string;
  status?: SubsectionStatus;
  orderNo: number;
  subSectionTitle: string;
  mode: "EDIT" | "PREVIEW" | "NEW";
  workshopSectionContent: WorkshopSectionContent[];
  edited: boolean;
  archived?: boolean;
  isDirectArchived?: boolean;
  createdAt?: Date;
  archivedAt?: Date;
  archivedBy?: Date;
}

const initialSubsections: WorkshopSection[] = [];

interface ConformModel {
  show: boolean;
  title: string;
  content: any;
  onCancel: () => void;
  onConfirm: () => void;
  hideModel: () => void;
  showModel: (
    title: string,
    body: any,
    onCancel: () => void,
    onConfirm: () => void
  ) => void;
}

const initialConfirmModel: ConformModel = {
  show: false,
  title: "",
  content: <p />,
  onCancel: () => {
    // empty function body
  },
  onConfirm: () => {
    // empty function body
  },
  hideModel: () => {
    // empty function body
  },
  showModel: (
    title: string,
    body: any,
    onCancel: () => void,
    onConfirm: () => void
  ) => {
    // empty function body
  },
};

interface IWorkshopContext {
  id: string;
  workshopSections: WorkshopSection[];
  selectedSubSection: WorkshopSection;
  country: Country;
  createdAt: any;
  description: string;
  startDate: any;
  endDate: any;
  orderNo: number;
  points: number;
  currentPoints: string;
  title: string;
  updatedAt: any;
  thumbnail: string;
  workshopStatus?: SubsectionStatus;
  workshopProgress: number;
  model: ConformModel;

  setSelectedSubSection(subSection: WorkshopSection): void;

  addSubSection(onFinshed: () => void, onFailed: () => void): void;

  updateSubSection(subSection: WorkshopSection): void;

  deleteSubSection(
    subSection: WorkshopSection,
    onFinish: () => void,
    onError: () => void
  ): void;

  changeSubsectionMode(subSection: WorkshopSection): void;

  addResource(
    subSection: WorkshopSection,
    type: WorkshopSectionContentType
  ): void;

  removeResource(
    subSection: WorkshopSection,
    resource: WorkshopSectionContent
  ): void;

  updateResource(
    subSection: WorkshopSection,
    resource: WorkshopSectionContent
  ): void;

  updateResourceContent(orderNo: number, content: string): void;

  moveResource(from: number, to: number): void;

  saveData(onFinised: () => void, onError: () => void): void;

  saveDataAndSelectSubSection(
    subsectionIndexToSelect: number,
    onFinish: () => void
  ): void;

  uploadResource: (
    subSection: WorkshopSection,
    resource: WorkshopSectionContent,
    onFinished: () => void,
    onErorr: () => void,
    onProgress: (value: number) => void
  ) => void;
  clearAllResources: () => void;
  discardChangesAndSelectSubSection: (subsectionIndexToSelect: number) => void;
  discardChangesAndCreateNewWorkshop: () => void;
  discardChanges: () => void;
  discardResourceData: (
    subsection: WorkshopSection,
    resource: WorkshopSectionContent
  ) => void;
  setResourceFileName: (
    resource: WorkshopSectionContent,
    fileName: string
  ) => void;
}

const initialContext: IWorkshopContext = {
  id: "",
  workshopSections: initialSubsections,
  selectedSubSection: {
    id: "",
    workshopSectionContent: [],
    subSectionTitle: "",
    orderNo: -1,
    mode: "EDIT",
    edited: false,
  },
  workshopProgress: 0,
  model: initialConfirmModel,
  country: Country.MALAYSIA,
  createdAt: new Date(),
  description: "",
  startDate: new Date(),
  endDate: new Date(),
  thumbnail: "",
  orderNo: -1,
  points: 0,
  currentPoints: "0",
  title: "",
  updatedAt: new Date(),
  setSelectedSubSection: (subSection: WorkshopSection) => {
    // empty function body
  },
  changeSubsectionMode: (subSection: WorkshopSection) => {
    // empty function body
  },
  addSubSection: (onFinshed: () => void, onFailed: () => void) => {
    // empty function body
  },
  updateSubSection: (subSection: WorkshopSection) => {
    // empty function body
  },
  deleteSubSection: (
    subSection: WorkshopSection,
    onFinish: () => void,
    onError: () => void
  ) => {
    // empty function body
  },
  addResource: (
    subSection: WorkshopSection,
    type: WorkshopSectionContentType
  ) => {
    // empty function body
  },
  updateResourceContent: (orderNo: number, content: string) => {
    // empty function body
  },
  removeResource: (
    subSection: WorkshopSection,
    resource: WorkshopSectionContent
  ) => {
    // empty function body
  },
  updateResource: (
    subSection: WorkshopSection,
    resource: WorkshopSectionContent
  ) => {
    // empty function body
  },
  moveResource: (from: number, to: number) => {
    // empty function body
  },
  saveData: (onFinised: () => void, onError: () => void) => {
    // empty function body
  },
  saveDataAndSelectSubSection: (
    subsectionIndexToSelect: number,
    onFinish: () => void
  ) => {
    // empty function body
  },
  uploadResource: (
    subSection: WorkshopSection,
    resource: WorkshopSectionContent,
    onFinished: () => void,
    onErorr: () => void,
    onProgress: (value: number) => void
  ) => {
    // empty function body
  },
  clearAllResources: () => {
    // empty function body
  },
  discardChangesAndSelectSubSection: (subsectionIndexToSelect: number) => {
    // empty function body
  },
  discardChangesAndCreateNewWorkshop: () => {
    // empty function body
  },
  discardChanges: () => {
    // empty function body
  },
  discardResourceData: (
    subsection: WorkshopSection,
    resource: WorkshopSectionContent
  ) => {
    // empty function body
  },
  setResourceFileName: (resource: WorkshopSectionContent, fileName: string) => {
    // empty function body
  },
};

const WorkshopContext = createContext<IWorkshopContext>(initialContext);

interface IState {
  id: string;
  country: Country;
  createdAt: any;
  description: string;
  startDate: any;
  endDate: any;
  orderNo: number;
  points: number;
  currentPoints: string;
  workshopStatus?: SubsectionStatus;
  title: string;
  updatedAt: any;
  workshopSections: WorkshopSection[];
  selectedSubSection: WorkshopSection;
  workshopProgress: number;
  thumbnail: string;
}

const initialState: IState = {
  id: "",
  workshopSections: initialSubsections,
  selectedSubSection: {
    id: "",
    workshopSectionContent: [],
    subSectionTitle: "",
    orderNo: -1,
    mode: "EDIT",
    edited: false,
  },
  country: Country.MALAYSIA,
  createdAt: new Date(),
  description: "",
  startDate: new Date(),
  endDate: new Date(),
  orderNo: -1,
  points: 0,
  currentPoints: "0",
  title: "",
  updatedAt: new Date(),
  workshopProgress: 0,
  thumbnail: "",
};

interface ConformModelState {
  show: boolean;
  title: string;
  content: any;
  onCancel: () => void;
  onConfirm: () => void;
}

const initialConfirmModelState: ConformModelState = {
  show: false,
  title: "",
  content: <p />,
  onCancel: () => {
    // empty function body
  },
  onConfirm: () => {
    // empty function body
  },
};

interface IProps {
  children: any;
  isArchived: boolean;
  isDireactArchived: boolean;
  workshopId: string;
  type?: "PUBLIC" | "PRIVATE";
}

export const WorkshopProvider = (props: IProps) => {
  const store = useFirestore();
  const { workshopId } = props;
  const [state, setState] = useState(initialState);
  const [model, setModel] = useState(initialConfirmModelState);
  const [loadData, setLoadData] = useState(false);

  useEffect(() => {
    return store
      .collection(
        props.type && props.type === "PUBLIC" ? "publicWorkshops" : "workshops"
      )
      .doc(workshopId)
      .onSnapshot((doc) => {
        setState((ps) => {
          let data: IState = doc.data() as IState;
          if (data) {
            if (data.workshopSections) {
              if (props.isArchived) {
                data.workshopSections = data.workshopSections.filter(
                  (s) =>
                    s.archived && s.isDirectArchived === props.isDireactArchived
                );
              } else {
                data.workshopSections = data.workshopSections
                  .filter((s) => !s.archived)
                  .sort((a, b) => a.orderNo - b.orderNo);

                data.workshopSections.forEach((s, index) => {
                  s.orderNo = index;
                });
              }
              data.workshopSections = data.workshopSections.map((s) => ({
                ...s,
                mode:
                  s.subSectionTitle === "Sub Section Title"
                    ? "EDIT"
                    : "PREVIEW",
              }));
            }

            if (!data.workshopSections) {
              return {
                ...ps,
                ...data,
                id: workshopId,
                workshopSections: [],
                startDate: (data.startDate as firebase.firestore.Timestamp).toDate(),
                endDate: (data.endDate as firebase.firestore.Timestamp).toDate(),
              };
            } else {
              return {
                ...ps,
                ...data,
                id: workshopId,
                startDate: (data.startDate as firebase.firestore.Timestamp).toDate(),
                endDate: (data.endDate as firebase.firestore.Timestamp).toDate(),
              };
            }
          } else {
            return { ...ps, id: workshopId };
          }
        });
        setLoadData(true);
      });
  }, [
    workshopId,
    store,
    props.type,
    props.isArchived,
    props.isDireactArchived,
  ]);

  useEffect(() => {
    setModel((ps) => ({ ...ps, show: false }));
  }, []);

  const setSelectedSubSection = (subSection: WorkshopSection) => {
    setState({ ...state, selectedSubSection: subSection });
  };

  const addSubSection = (onFinshed: () => void, onFailed: () => void) => {
    setState((ps) => {
      ps.workshopSections.push({
        id: "",
        subSectionTitle: "Sub Section Title",
        workshopSectionContent: [],
        orderNo: ps.workshopSections.length,
        mode: "EDIT",
        edited: false,
      });
      setSelectedSubSection(
        ps.workshopSections[ps.workshopSections.length - 1]
      );
      saveData(onFailed, onFailed);
      return { ...ps };
    });
  };

  const changeSubsectionMode = (subSection: WorkshopSection) => {
    setState((ps) => {
      const [s] = ps.workshopSections.filter(
        (s) => s.orderNo === subSection.orderNo
      );
      s.mode = subSection.mode;
      ps.selectedSubSection.mode = subSection.mode;
      return { ...ps };
    });
  };

  const updateSubSection = (subSection: WorkshopSection) => {
    setState((ps) => {
      const selectedSubsection = getSubsection(ps, subSection.orderNo);
      selectedSubsection.subSectionTitle = subSection.subSectionTitle;
      selectedSubsection.workshopSectionContent =
        subSection.workshopSectionContent;
      selectedSubsection.mode = subSection.mode;
      selectedSubsection.edited = true;
      ps.selectedSubSection = selectedSubsection;
      return { ...ps };
    });
  };

  const getSubsection = (ps: IState, orderNo: number) => {
    const [selectedSubsection] = ps.workshopSections.filter(
      (s) => s.orderNo === orderNo
    );
    return selectedSubsection;
  };

  const deleteSubSection = (
    subSection: WorkshopSection,
    onFinish: () => void,
    onError: () => void
  ) => {
    setState((ps) => {
      const index = ps.workshopSections.findIndex(
        (s) => s.orderNo === subSection.orderNo
      );
      if (index >= 0) {
        ps.workshopSections.splice(index, 1);
        ps.workshopSections.forEach((section, index) => {
          section.orderNo = index;
        });
        const nextSelect =
          subSection.orderNo <= ps.workshopSections.length - 1
            ? subSection.orderNo
            : subSection.orderNo - 1;
        ps.selectedSubSection =
          nextSelect !== -1
            ? state.workshopSections[nextSelect]
            : {
                id: "",
                orderNo: -1,
                subSectionTitle: "",
                workshopSectionContent: [],
                mode: "EDIT",
                edited: false,
              };
        return { ...ps };
      }
      return ps;
    });
    saveData(
      () => {
        onFinish();
      },
      () => {
        onError();
      }
    );
  };

  const discardChangesAndSelectSubSection = (
    subsectionIndexToSelect: number
  ) => {
    store
      .collection(
        props.type && props.type === "PUBLIC" ? "publicWorkshops" : "workshops"
      )
      .doc(workshopId)
      .get({ source: "cache" })
      .then((doc) => {
        setState((ps) => {
          let data: IState = doc.data() as IState;
          if (data) {
            if (data.workshopSections)
              data.workshopSections = data.workshopSections.map((s) => ({
                ...s,
                mode:
                  s.subSectionTitle === "Sub Section Title"
                    ? "EDIT"
                    : "PREVIEW",
              }));
            if (!data.workshopSections) {
              ps = {
                ...ps,
                ...data,
                id: workshopId,
                workshopSections: [],
                startDate: (data.startDate as firebase.firestore.Timestamp).toDate(),
                endDate: (data.endDate as firebase.firestore.Timestamp).toDate(),
              };
            } else {
              ps = {
                ...ps,
                ...data,
                id: workshopId,
                startDate: (data.startDate as firebase.firestore.Timestamp).toDate(),
                endDate: (data.endDate as firebase.firestore.Timestamp).toDate(),
              };
            }
          } else {
            ps = { ...ps, id: workshopId };
          }

          ps.selectedSubSection = ps.workshopSections[subsectionIndexToSelect];

          return { ...ps };
        });
      });
  };

  const discardChanges = () => {
    store
      .collection(
        props.type && props.type === "PUBLIC" ? "publicWorkshops" : "workshops"
      )
      .doc(workshopId)
      .get({ source: "cache" })
      .then((doc) => {
        setState((ps) => {
          let data: IState = doc.data() as IState;

          if (data) {
            if (data.workshopSections)
              data.workshopSections = data.workshopSections.map((s) => ({
                ...s,
                mode: "PREVIEW",
                workshopSectionContent: s.workshopSectionContent.map((r) => ({
                  ...r,
                  mode: "PREVIEW",
                })),
              }));
            if (!data.workshopSections) {
              ps = {
                ...ps,
                ...data,
                id: workshopId,
                workshopSections: [],
                startDate: (data.startDate as firebase.firestore.Timestamp).toDate(),
                endDate: (data.endDate as firebase.firestore.Timestamp).toDate(),
              };
            } else {
              ps = {
                ...ps,
                ...data,
                id: workshopId,
                startDate: (data.startDate as firebase.firestore.Timestamp).toDate(),
                endDate: (data.endDate as firebase.firestore.Timestamp).toDate(),
              };
            }
          } else {
            ps = { ...ps, id: workshopId };
          }

          const section = getSubsection(ps, ps.selectedSubSection.orderNo);
          ps.selectedSubSection = section;
          ps.selectedSubSection.mode = "PREVIEW";
          ps.selectedSubSection.edited = false;
          section.mode = "PREVIEW";
          section.edited = false;

          return { ...ps };
        });
      });
  };

  const discardChangesAndCreateNewWorkshop = () => {
    store
      .collection(
        props.type && props.type === "PUBLIC" ? "publicWorkshops" : "workshops"
      )
      .doc(workshopId)
      .get({ source: "cache" })
      .then((doc) => {
        setState((ps) => {
          let data: IState = doc.data() as IState;
          if (data) {
            if (data.workshopSections)
              data.workshopSections = data.workshopSections.map((s) => ({
                ...s,
                mode: "PREVIEW",
                workshopSectionContent: s.workshopSectionContent.map((r) => ({
                  ...r,
                  mode: "PREVIEW",
                })),
              }));
            if (!data.workshopSections) {
              ps = {
                ...ps,
                ...data,
                id: workshopId,
                workshopSections: [],
                startDate: (data.startDate as firebase.firestore.Timestamp).toDate(),
                endDate: (data.endDate as firebase.firestore.Timestamp).toDate(),
              };
            } else {
              ps = {
                ...ps,
                ...data,
                id: workshopId,
                startDate: (data.startDate as firebase.firestore.Timestamp).toDate(),
                endDate: (data.endDate as firebase.firestore.Timestamp).toDate(),
              };
            }
          } else {
            ps = { ...ps, id: workshopId };
          }

          ps.workshopSections.push({
            subSectionTitle: "",
            workshopSectionContent: [],
            orderNo: ps.workshopSections.length,
            mode: "EDIT",
            edited: true,
          });
          ps.selectedSubSection =
            ps.workshopSections[ps.workshopSections.length - 1];

          return { ...ps };
        });
      });
  };

  const discardResourceData = (
    subsection: WorkshopSection,
    resource: WorkshopSectionContent
  ) => {
    store
      .collection(
        props.type && props.type === "PUBLIC" ? "publicWorkshops" : "workshops"
      )
      .doc(workshopId)
      .get({ source: "cache" })
      .then((doc) => {
        let data: IState = doc.data() as IState;
        if (data) {
          if (data.workshopSections) {
            const oldResources = data.workshopSections
              .filter((s) => s.id === subsection.id)[0]
              .workshopSectionContent.filter((r) => r.id === resource.id);

            if (oldResources.length === 0) {
              removeResource(subsection, resource);
            } else {
              let subs = state.workshopSections.filter(
                (s) => s.id === subsection.id
              )[0];
              subs.workshopSectionContent = subs.workshopSectionContent.map(
                (r) => {
                  if (r.id === resource.id) {
                    return {
                      ...r,
                      content: oldResources[0].content,
                      fileName: "",
                      mode: "PREVIEW",
                    };
                  } else {
                    return r;
                  }
                }
              );

              state.workshopSections = state.workshopSections.map((s) => {
                if (s.id === subsection.id) {
                  return subs;
                } else {
                  return s;
                }
              });

              state.selectedSubSection = getSubsection(
                state,
                state.selectedSubSection.orderNo
              );
            }
          }
        }
        setState({ ...state });
      });
  };

  const addResource = (
    subSection: WorkshopSection,
    type: WorkshopSectionContentType
  ) => {
    setState((ps) => {
      const section = getSubsection(ps, subSection.orderNo)
        .workshopSectionContent;
      section.push({
        mode: "EDIT",
        type: type,
        content: "",
        orderNo: section.length,
      });
      const s_section = getSubsection(ps, ps.selectedSubSection.orderNo);
      s_section.workshopSectionContent.splice(
        0,
        0,
        s_section.workshopSectionContent.splice(
          s_section.workshopSectionContent.length - 1,
          1
        )[0]
      );
      s_section.workshopSectionContent.forEach((resource, index) => {
        resource.orderNo = index;
      });
      setSelectedSubSection(s_section);
      return { ...ps };
    });
  };

  const removeResource = (
    subSection: WorkshopSection,
    resource: WorkshopSectionContent
  ) => {
    setState((ps) => {
      const section = getSubsection(ps, subSection.orderNo);
      section.workshopSectionContent.splice(resource.orderNo, 1);
      getSubsection(
        ps,
        ps.selectedSubSection.orderNo
      ).workshopSectionContent.forEach((resource, index) => {
        resource.orderNo = index;
      });
      section.edited = true;
      return { ...ps };
    });
  };

  const uploadResource = (
    subSection: WorkshopSection,
    resource: WorkshopSectionContent,
    onFinished: () => void,
    onErorr: () => void,
    onProgress: (value: number) => void
  ) => {
    const content: any = JSON.parse(resource.content);
    fetch(content.url).then((resp) => {
      resp.blob().then((file) => {
        const uploadTask = storage
          .child(`resources/${random.generate(10)}.${content.type}`)
          .put(file);
        uploadTask.on("state_changed", function(snapshot) {
          var progress = Math.round(
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100
          );
          onProgress(progress);
        });
        uploadTask
          .then((snapshot) => {
            snapshot.ref
              .getDownloadURL()
              .then((url) => {
                setState((ps) => {
                  const section = getSubsection(ps, subSection.orderNo);
                  const content =
                    section.workshopSectionContent[resource.orderNo].content;
                  const data: MediaFileContent = JSON.parse(content);
                  data.url = url;
                  ps.workshopSections[
                    subSection.orderNo
                  ].workshopSectionContent[
                    resource.orderNo
                  ].content = JSON.stringify(data);

                  section.workshopSectionContent[resource.orderNo].mode =
                    resource.mode;

                  return { ...ps };
                });
                onFinished();
              })
              .catch((erro) => {
                onErorr();
              });
          })
          .catch((error) => {
            onErorr();
          });
      });
    });
  };

  const updateResourceContent = (orderNo: number, content: string) => {
    setState((ps) => {
      getSubsection(ps, ps.selectedSubSection.orderNo).workshopSectionContent[
        orderNo
      ].content = content;
      ps.selectedSubSection.workshopSectionContent[orderNo].content = content;
      return { ...ps };
    });
  };

  const updateResource = (
    subSection: WorkshopSection,
    resource: WorkshopSectionContent
  ) => {
    setState((ps) => {
      const section = getSubsection(ps, subSection.orderNo);
      section.workshopSectionContent[resource.orderNo].content =
        resource.content;
      section.workshopSectionContent[resource.orderNo].mode = resource.mode;
      section.workshopSectionContent[resource.orderNo].type = resource.type;
      if (section.workshopSectionContent[resource.orderNo].wached)
        section.workshopSectionContent[resource.orderNo].wached =
          resource.wached;
      section.edited = true;
      section.edited = true;

      setSelectedSubSection(section);
      return Object.assign({}, ps);
    });
  };

  const moveResource = (from: number, to: number) => {
    setState((ps) => {
      const section = getSubsection(ps, ps.selectedSubSection.orderNo);
      section.workshopSectionContent.splice(
        to,
        0,
        section.workshopSectionContent.splice(from, 1)[0]
      );
      section.workshopSectionContent.forEach((resource, index) => {
        resource.orderNo = index;
      });

      setSelectedSubSection(section);
      return { ...ps };
    });
  };

  const clearAllResources = () => {
    setState((ps) => {
      getSubsection(
        ps,
        ps.selectedSubSection.orderNo
      ).workshopSectionContent = [];
      // ps.workshopSections[ps.selectedSubSection.orderNo].subSectionTitle = "";
      return { ...ps };
    });
  };

  const setResourceFileName = (
    resource: WorkshopSectionContent,
    fileName: string
  ) => {
    setState((ps) => {
      const section = getSubsection(ps, ps.selectedSubSection.orderNo);
      section.workshopSectionContent[resource.orderNo].fileName = fileName;
      ps.selectedSubSection.workshopSectionContent[
        resource.orderNo
      ].fileName = fileName;
      return { ...ps };
    });
  };
  const storage = useStorage().ref();

  const updateWorkshopFunctionRef = useFunctions().httpsCallable(
    props.type === "PUBLIC"
      ? "updatePublicWorkshopSections"
      : "updateWorkshopSections"
  );
  const saveData = (onFinised: () => void, onError: () => void) => {
    const data = {
      id: props.workshopId,
      noOfSections: 1,
      workshopSections: state.workshopSections.map((s) => ({
        id: s.id,
        orderNo: s.orderNo,
        subSectionTitle: s.subSectionTitle,
        workshopSectionContent: s.workshopSectionContent
          .filter((x) => x.content.length > 0)
          .map((c) => ({
            type: c.type,
            content: c.content,
            orderNo: c.orderNo,
          })),
      })),
    };

    updateWorkshopFunctionRef(data)
      .then(() => {
        onFinised();
        setState((ps) => {
          const subs = getSubsection(ps, ps.selectedSubSection.orderNo);
          if (subs) {
            if (subs?.subSectionTitle === "Sub Section Title") {
              subs.edited = false;
              subs.mode = "EDIT";
            } else {
              subs.edited = false;
              subs.mode = "PREVIEW";
            }
          }
          setSelectedSubSection(subs);
          return { ...ps };
        });
      })
      .catch((err) => {
        onError();
      });
  };

  const saveDataAndSelectSubSection = (
    subsectionIndexToSelect: number,
    onFinish: () => void
  ) => {
    const data = {
      id: props.workshopId,
      noOfSections: 1,
      workshopSections: state.workshopSections.map((s) => ({
        id: s.id,
        orderNo: s.orderNo,
        subSectionTitle: s.subSectionTitle,
        workshopSectionContent: s.workshopSectionContent
          .filter((x) => x.content.length > 0)
          .map((c) => ({
            type: c.type,
            content: c.content,
            orderNo: c.orderNo,
          })),
      })),
    };

    updateWorkshopFunctionRef(data)
      .then(() => {
        onFinish();
        setState((ps) => {
          const [subs] = ps.workshopSections.filter(
            (ws) => ws.orderNo === ps.selectedSubSection.orderNo
          );
          if (subs) {
            if (subs?.subSectionTitle === "Sub Section Title") {
              subs.edited = false;
              subs.mode = "EDIT";
            } else {
              subs.edited = false;
              subs.mode = "PREVIEW";
            }
          }
          ps.selectedSubSection = ps.workshopSections[subsectionIndexToSelect];
          return { ...ps };
        });
      })
      .catch((err) => {
        onFinish();
      });
  };

  const hideModel = () => {
    setModel((ps) => ({ ...ps, show: false }));
  };
  const showModel = (
    title: string,
    body: any,
    onCancel: () => void,
    onConfirm: () => void
  ) => {
    setModel((ps) => {
      ps.show = true;
      ps.title = title;
      ps.content = body;
      ps.onCancel = onCancel;
      ps.onConfirm = onConfirm;
      return { ...ps };
    });
  };

  const { workshopProgress } = useAppUser();

  useEffect(() => {
    let mounted = true;
    if (mounted && loadData) {
      setState((ps) => {
        console.log(ps);
        const completed =
          workshopProgress
            ?.filter((w) => w.workshopId === ps.id)
            .map((w) => {
              ps.workshopStatus = w.status;
              ps.workshopSections = ps.workshopSections.map((sub) => {
                w.subsectionsProgress.forEach((s) => {
                  if (sub.id === s.subsectionId) {
                    sub.status = s.status;
                    if (s.watchedVideoIds && s.watchedVideoIds.length > 0) {
                      sub.workshopSectionContent = sub.workshopSectionContent.map(
                        (r) => {
                          if (
                            s.watchedVideoIds &&
                            s.watchedVideoIds.filter((i) => i === r.id).length >
                              0
                          ) {
                            r.wached = true;
                          }
                          return r;
                        }
                      );
                    }
                  }
                });
                return { ...sub };
              });
              return w.subsectionsProgress.filter(
                (s) => s.status === SubsectionStatus.DONE
              ).length;
            })[0] || 0;

        console.log("completed:", completed);
        ps.workshopProgress = Math.round(
          (completed / ps.workshopSections.length) * 100
        );
        ps.currentPoints = (
          (completed / ps.workshopSections.length) *
          ps.points
        ).toFixed(2);

        return { ...ps };
      });
    }
    return () => {
      mounted = false;
    };
  }, [workshopProgress, loadData]);

  return (
    <WorkshopContext.Provider
      value={{
        ...state,
        setSelectedSubSection,
        addSubSection,
        updateSubSection,
        deleteSubSection,
        addResource,
        removeResource,
        updateResource,
        updateResourceContent,
        moveResource,
        saveData,
        saveDataAndSelectSubSection,
        uploadResource,
        clearAllResources,
        changeSubsectionMode,
        discardChangesAndCreateNewWorkshop,
        discardChangesAndSelectSubSection,
        discardChanges,
        discardResourceData,
        setResourceFileName,
        model: { ...model, showModel, hideModel },
      }}
    >
      {props.children}
    </WorkshopContext.Provider>
  );
};

export const useWorkShop = (): IWorkshopContext => useContext(WorkshopContext);
